import { useRef, useEffect } from 'react';
import { resizeCanvas, resizeCanvasToDisplaySize } from './util'
/**
 * Determines if a point is within the specified range of a ball's center.
 *
 * @param {number} pointX - The X coordinate of the point.
 * @param {number} pointY - The Y coordinate of the point.
 * @param {number} ballX - The X coordinate of the ball's center.
 * @param {number} ballY - The Y coordinate of the ball's center.
 * @param {number} [radius=5] - The radius of the ball, default is 5.
 * @returns {boolean} - True if the point is within the ball's range; otherwise, false.
 */
const isWithinBall = (pointX, pointY, ballX, ballY, radius = 13) => {
    const dx = pointX - ballX;
    const dy = pointY - ballY;
    return (dx * dx + dy * dy) <= (radius * radius);
};

const isWithinBall_Mobile = (pointX, pointY, ballX, ballY, radius = 30) => {
    const dx = pointX - ballX;
    const dy = pointY - ballY;
    return (dx * dx + dy * dy) <= (radius * radius);
};

/**
 * Custom hook for handling canvas drawing and interaction.
 * This hook registers event listeners for mouse interactions and manages drag operations.
 *
 * @param {Function} draw - Function to render the canvas content.
 * @param {Array} filters - Array of filter points for drawing.
 * @param {Function} onDrag - Callback to handle point position updates during dragging.
 * @param {Function} canvasMapping - Maps filter frequency and gain to canvas coordinates.
 * @param {number|null} draggingIndex - Index of the currently dragged point, or null if not dragging.
 * @param {Function} setDraggingIndex - State setter to update the dragging index.
 * @returns {Object} - A reference to the canvas DOM element.
 */
const useCanvas = (drawHandles, globalMode, filters, onDrag, canvasMapping, draggingIndex, setDraggingIndex) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        resizeCanvas(canvas);
        resizeCanvasToDisplaySize(canvas);
        /**
         * Retrieves mouse coordinates relative to the canvas element.
         * 
         * @param {MouseEvent} event - The mouse event object.
         * @returns {Object} Coordinates with properties mouseX and mouseY.
         */
        const getMouseCoord = (event) => {
            const rect = canvas.getBoundingClientRect();
            const mouseX = event.clientX - rect.left;
            const mouseY = event.clientY - rect.top;
            return { mouseX, mouseY };
        };

        const getTouchCoord = (event) => {
            const rect = canvas.getBoundingClientRect();
            const touch = event.touches[0] || event.changedTouches[0];
            const touchX = touch.clientX - rect.left;
            const touchY = touch.clientY - rect.top;
            return { mouseX: touchX, mouseY: touchY };
        };

        /**
         * Initiates dragging on mouse down if a ball is clicked.
         * 
         * @param {MouseEvent} event - The mouse down event object.
         */
        const handleMouseDown = (event) => {
            const { mouseX, mouseY } = getMouseCoord(event);

            const foundIndex = filters.findIndex((filter) => {
                const { x, y } = canvasMapping(context, filter.frequency, filter.gain);
                return isWithinBall(mouseX, mouseY, x, y);
            });

            if (foundIndex !== -1 && event.buttons === 1) {
                setDraggingIndex(foundIndex);
            }
        };

        /**
         * Updates position during dragging on mouse move.
         * 
         * @param {MouseEvent} event - The mouse move event object.
         */
        const handleMouseMove = (event) => {
            if (draggingIndex !== null) {
                const { mouseX, mouseY } = getMouseCoord(event);
                onDrag(context, mouseX, mouseY);
            }
        };

        /**
         * Ends dragging on mouse up.
         */
        const handleMouseUp = () => {
            setDraggingIndex(null);
        };

        const handleTouchStart = (event) => {
            event.preventDefault();
            const { mouseX, mouseY } = getTouchCoord(event);

            const foundIndex = filters.findIndex((filter) => {
                const { x, y } = canvasMapping(context, filter.frequency, filter.gain);
                return isWithinBall_Mobile(mouseX, mouseY, x, y);
            });

            if (foundIndex !== -1) {
                setDraggingIndex(foundIndex);
            }
        };

        const handleTouchMove = (event) => {
            if (draggingIndex !== null) {
                event.preventDefault();
                const { mouseX, mouseY } = getTouchCoord(event);
                onDrag(context, mouseX, mouseY);
            }
        };

        const handleTouchEnd = () => {
            setDraggingIndex(null);
        };

        /**
         * Ends dragging if the mouse leaves the canvas.
         */
        const handleMouseLeave = () => {
            setDraggingIndex(null);
        };


        // Register event listeners
        canvas.addEventListener('mousedown', handleMouseDown);
        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseup', handleMouseUp);
        canvas.addEventListener("mouseleave", handleMouseLeave);


        // Register event listeners for touch
        canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
        canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
        canvas.addEventListener('touchend', handleTouchEnd);


        // Render the canvas content
        drawHandles(context);

        return () => {
            console.log('base canvas unmounted')
            // Remove event listeners to avoid memory leaks
            canvas.removeEventListener('mousedown', handleMouseDown);
            canvas.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mouseup', handleMouseUp);
            canvas.removeEventListener("mouseleave", handleMouseLeave);

            canvas.removeEventListener('touchstart', handleTouchStart);
            canvas.removeEventListener('touchmove', handleTouchMove);
            canvas.removeEventListener('touchend', handleTouchEnd);


            context.clearRect(0, 0, canvas.width, canvas.height);

        };
    }, [drawHandles, globalMode, filters, onDrag, canvasMapping, draggingIndex, setDraggingIndex]);

    return canvasRef;
};

/**
 * Canvas component for rendering shapes and handling user interactions.
 * It accepts a drawing function, filters data, and drag event handlers, and
 * updates the canvas display as points are dragged by the user.
 *
 * @param {Function} draw - Function to render shapes on the canvas.
 * @param {Array} filters - Array of filters used to define shapes on the canvas.
 * @param {Function} onDrag - Drag handler function called when a point is moved.
 * @param {Function} canvasMapping - Function to map filter values to canvas coordinates.
 * @param {number|null} draggingIndex - Index of the currently dragged point, or null if none.
 * @param {Function} setDraggingIndex - Function to update the dragging point index.
 * @param {Object} rest - Additional properties for the canvas element.
 * @returns {JSX.Element} - A canvas element with interactive properties.
 */
const BaseCanvas = ({ drawHandles, globalMode, filters, onDrag, canvasMapping, draggingIndex, setDraggingIndex, ...rest }) => {
    const canvasRef = useCanvas(drawHandles, globalMode, filters, onDrag, canvasMapping, draggingIndex, setDraggingIndex);
    return <canvas className="base-canvas eq-canvas" ref={canvasRef} {...rest} />;
};

export default BaseCanvas;
